/** @format */

import { BreakpointsService, SessionStorageService } from '@shared/data-access/services';
import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import {
  compareBarProductAdded,
  compareBarProductRemoved,
  compareBarProductsCleared,
  compareDrawerClosed,
  compareDrawerOpened,
  compareProductDetailsFetchRetryTriggered,
  selectCompareBarProducts,
  selectCompareProductDetails,
  selectIsCompareGridOpen,
} from '@plp/data-access/compare';

import { BreakPointNew } from '@shared/data-access/enums';
import { COMPARE_PRODUCT_LIST_SESSION_STORAGE_KEY } from '@compare/data-access';
import { CompareBarProduct } from '@shared/data-access/interfaces';
import { Store } from '@ngrx/store';
import { isPlatformServer } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class ProductListCompareService {
  private readonly store = inject(Store);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly breakpointsService = inject(BreakpointsService);

  readonly compareBarList$ = this.store.selectSignal(selectCompareBarProducts);
  readonly compareGridList = this.store.selectSignal(selectCompareProductDetails);
  readonly isCompareGridOpen = this.store.selectSignal(selectIsCompareGridOpen);
  readonly shouldDrawerBeOpen = signal(false);

  readonly drawerSize = computed(() => {
    const bp = this.breakpointsService.breakpoint();
    const compareBarListLength = this.compareBarList$().length;

    if (bp <= BreakPointNew.MEDIUM) {
      // until 1024px
      return 'large';
    } else if (bp <= BreakPointNew.EXTRALARGE) {
      if (compareBarListLength <= 2) {
        return 'medium';
      } else {
        return 'large';
      }
    } else if (compareBarListLength <= 2) {
      return 'small';
    } else if (compareBarListLength === 3) {
      return 'medium';
    } else {
      return 'large';
    }
  });

  readonly drawerDirection = computed(() =>
    this.breakpointsService.breakpoint() >= BreakPointNew.LARGE ? 'right' : 'bottom',
  );

  addProductToCompareList(compareBarProduct: CompareBarProduct): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    this.store.dispatch(compareBarProductAdded({ compareBarProduct }));
  }

  removeProductFromCompareList(ecode: string | undefined): void {
    if (isPlatformServer(this.platformId) || !ecode) {
      return;
    }

    this.store.dispatch(compareBarProductRemoved({ ecode }));
  }

  getCompareListFromSessionStorage() {
    if (isPlatformServer(this.platformId)) {
      return [];
    }

    const storedCompareBarProducts = this.sessionStorageService.get(
      COMPARE_PRODUCT_LIST_SESSION_STORAGE_KEY,
      '',
    );

    const compareBarProducts: CompareBarProduct[] = storedCompareBarProducts
      ? JSON.parse(storedCompareBarProducts)
      : [];

    return compareBarProducts;
  }

  clearCompareList(): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    this.store.dispatch(compareBarProductsCleared());
  }

  openCompareGrid(): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    this.shouldDrawerBeOpen.set(true);
    this.store.dispatch(compareDrawerOpened());
  }

  closeCompareGrid(): void {
    if (!this.isCompareGridOpen()) {
      return;
    }

    this.shouldDrawerBeOpen.set(false);
    this.store.dispatch(compareDrawerClosed());
  }

  modifyImgSrcWidthAndHeight(imgURL: string): string {
    const newImageParams = '?wid=355&hei=355&qlt=85,0&fmt=webp&op_sharpen=1';
    return imgURL.split('?')[0] + newImageParams;
  }

  /**
   * Retry the last product details fetch using the cached parameters.
   */
  retryLastProductDetailsFetch(): void {
    this.store.dispatch(compareProductDetailsFetchRetryTriggered());
  }
}
